<template>
  <section ref="page" class="ui-page first-reply" tabindex="-1" aria-label="Trengo first reply analysis">
    <AppFilterToolbar>
      <template #primary><AppInput :value="search" design="new" type="search" maxlength="255" placeholder="Lead, ticket ID or subject" aria-label="Search" @input="queueSearch" @keydown.enter.native="submitSearch" /></template>
      <AppSelect :value="selectedManagers" design="new" label="full_name" input-id="first-reply-manager" aria-label="Manager" :options="managerOptions" :reduce="option => option.id" multiple placeholder="Manager" @input="changeFilters({ manager: ($event || []).join(',') })" />
      <template v-if="view === 'analysed'">
        <AppSelect :value="query.status || null" design="new" label="label" aria-label="Status" :options="statusOptions" :reduce="option => option.value" placeholder="Status" @input="changeFilters({ status: $event })" />
        <div class="first-reply__field"><AppInput :value="query.score_min" design="new" type="number" min="0" max="10" placeholder="Score from" aria-label="Score from" @input="changeFilters({ score_min: $event })" /></div>
        <div class="first-reply__field"><AppInput :value="query.score_max" design="new" type="number" min="0" max="10" placeholder="Score to" aria-label="Score to" @input="changeFilters({ score_max: $event })" /></div>
      </template>
      <template #selection><AppFilters name="first-reply-view" design="new" refined weight="medium" :filters="viewOptions" :value="view" aria-label="Ticket set" @change="changeView" /></template>
      <template v-if="view === 'pending'" #secondary-selection><AppFilters name="first-reply-scope" design="new" refined weight="medium" :filters="scopeOptions" :value="historical ? 'historical' : 'recent'" aria-label="Ticket period" @change="changeScope" /></template>
      <template v-if="canEdit && view === 'pending' && rows.length" #actions><AppIconButton :label="`Run selected (${selected.length})`" tooltip-placement="left" :disabled="!selected.length || running" :aria-busy="running ? 'true' : null" @click="run(selected)"><i class="pi pi-play" aria-hidden="true" /></AppIconButton></template>
      <template v-if="hasFilters || search" #clear><AppClearFiltersButton design="new" icon-only @clear="clearFilters" /></template>
    </AppFilterToolbar>
    <AppContentSkeleton v-if="loading" label="Loading tickets" :columns="columns" min-width="1200px" />
    <AppErrorState v-else-if="error" :message="error" icon-only @retry="load()" />
    <AppCard v-else-if="rows.length" design="new" variant="table" header-density="compact">
      <AppDataTable design="new" density="dense" :columns="columns" :rows="rows" row-key="id" :clickable="view === 'analysed'" min-width="1200px" @row-click="openRow">
        <template #header-select><span class="first-reply__check"><AppCheckbox single design="new" label="Select all tickets on this page" :value="allSelected" @change="toggleAll" /></span></template>
        <template #cell="{ row, column }">
          <span v-if="column.key === 'select'" class="first-reply__check"><AppCheckbox single design="new" :label="`Select ${row.lead.name || row.lead.id}`" :value="selected.includes(row.id)" @change="toggleOne(row.id, $event)" /></span>
          <AppTableText v-else-if="column.key === 'lead'" :text="row.lead.name || `Lead #${row.lead.id}`"><router-link class="ui-link" :to="{ name: 'crm-task-tracker-leads', query: { lead: String(row.lead.id) } }" @click.native.stop @keydown.enter.native.stop @keydown.space.native.stop>{{ row.lead.name || `Lead #${row.lead.id}` }}</router-link></AppTableText>
          <AppTableText v-else-if="column.key === 'ticket'" :text="row.ticket.external_id"><router-link class="ui-link" :to="{ name: 'crm-trengo-matching', query: { ticket: String(row.ticket.id) } }" @click.native.stop @keydown.enter.native.stop @keydown.space.native.stop>{{ row.ticket.external_id }}</router-link></AppTableText>
          <AppTableText v-else-if="column.key === 'manager'" :text="row.manager ? row.manager.full_name : ''" />
          <AppStatusBadge v-else-if="column.key === 'score'" design="new" compact :tone="row.score === null ? 'neutral' : scoreTone(row.score)" :label="row.score === null ? '—' : `${Number(row.score).toFixed(1)} / 10`" />
          <AppTableText v-else-if="column.key === 'analyzed_at'" :text="dateLabel(row.analyzed_at)" />
          <AppTableText v-else-if="column.key === 'first_reply_at'" :text="dateLabel(row.first_reply_at)" />
          <AppStatusBadge v-else-if="column.key === 'status'" design="new" compact :tone="statusOf(row.status).tone" :label="statusOf(row.status).label" />
          <AppIconButton v-else-if="column.key === 'actions' && view === 'analysed'" :disabled="isActive(row) || busy.includes(row.id)" :label="`Rerun analysis for ${row.lead.name || row.lead.id}`" @keydown.enter.native.stop @keydown.space.native.stop @click.stop="rerun(row)"><i class="pi pi-refresh" aria-hidden="true" /></AppIconButton>
          <AppIconButton v-else-if="column.key === 'actions'" :disabled="running || busy.includes(row.id)" :label="`Run analysis for ${row.lead.name || row.lead.id}`" @click.stop="run([row.id])"><i class="pi pi-play" aria-hidden="true" /></AppIconButton>
        </template>
      </AppDataTable>
    </AppCard>
    <AppNoResults v-else design="new" :title="emptyTitle" text="" />
    <AppPagination v-if="!loading && !error" design="new" placement="embedded" :page="page" :pages="pages" :count="count" :visible-count="rows.length" :page-size="PAGE_SIZE" :loading="loading" label="Ticket pages" @change="changePage" />
    <RatingModal v-if="ratingId" :id="ratingId" :key="ratingId" @close="closeRating" @changed="refresh" />
  </section>
</template>

<script>
import {
  getTrengoFirstReplyCandidates,
  getTrengoFirstReplyManagers,
  getTrengoFirstReplyRatings,
  postTrengoFirstReplyRerun,
  postTrengoFirstReplyRun
} from '@/http'
import { dateLabel, errorMessage } from '@/views/tech-support/integrations/model'
import { techSupportKey } from '@/utils/techSupportAccess'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import AppTableText from '@/components/AppTableText.vue'
import RatingModal from './RatingModal'
import { STATUS_OPTIONS, cleanQuery, isActive, scoreTone, statusOf } from './model'

const PAGE_SIZE = 40

const ANALYSED_COLUMNS = [
  { key: 'lead', label: 'Lead', width: '22%' },
  { key: 'ticket', label: 'Ticket', width: '15%' },
  { key: 'manager', label: 'Manager', width: '17%' },
  { key: 'score', label: 'Score', width: '10%' },
  { key: 'analyzed_at', label: 'Date', width: '14%' },
  { key: 'status', label: 'Status', width: '14%' },
  { key: 'actions', label: '', width: '8%', align: 'right' }
]

const PENDING_COLUMNS = [
  { key: 'select', label: '', width: '5%' },
  { key: 'lead', label: 'Lead', width: '25%' },
  { key: 'ticket', label: 'Ticket', width: '30%' },
  { key: 'first_reply_at', label: 'First reply', width: '25%' },
  { key: 'actions', label: '', width: '15%', align: 'right' }
]

export default {
  name: 'FirstReplyWorkspace',
  components: { AppContentSkeleton, AppErrorState, AppIconButton, AppTableText, RatingModal },
  data: () => ({
    PAGE_SIZE,
    rows: [],
    count: 0,
    pages: 1,
    counts: { analysed: null, pending: null },
    loading: false,
    error: '',
    selected: [],
    running: false,
    busy: [],
    search: '',
    searchTimer: null,
    pollTimer: null,
    generation: 0,
    countsGeneration: 0,
    statusOptions: STATUS_OPTIONS,
    managerOptions: []
  }),
  computed: {
    query() { return this.$route.query },
    view() { return this.query.view === 'pending' ? 'pending' : 'analysed' },
    historical() { return this.query.historical === 'true' },
    page() { return Number(this.query.page) || 1 },
    ratingId() { return this.query.rating || null },
    selectedManagers() { return (this.query.manager || '').split(',').filter(Boolean).map(Number) },
    canEdit() { return this.$store.getters['permissions/can'](techSupportKey('trengo', 'edit')) },
    columns() {
      const columns = this.view === 'pending' ? PENDING_COLUMNS : ANALYSED_COLUMNS
      return this.canEdit ? columns : columns.filter(column => !['select', 'actions'].includes(column.key))
    },
    params() { return this.paramsFor(this.view) },
    listKey() { return JSON.stringify([this.view, this.params]) },
    countsKey() { return JSON.stringify([this.paramsFor('analysed', true), this.paramsFor('pending', true)]) },
    allSelected() { return this.rows.length > 0 && this.rows.every(row => this.selected.includes(row.id)) },
    hasFilters() {
      return ['search', 'status', 'score_min', 'score_max', 'historical', 'manager'].some(key => this.query[key])
    },
    viewOptions() {
      const label = (name, count) => `${name} (${this.counts[count] === null ? '…' : this.counts[count]})`
      return [
        { value: 'analysed', label: label('Analysed', 'analysed') },
        { value: 'pending', label: label('Pending', 'pending') }
      ]
    },
    scopeOptions() {
      return [
        { value: 'recent', label: 'Since the start date' },
        { value: 'historical', label: 'Historical tickets' }
      ]
    },
    hint() {
      if (this.view === 'pending') return 'Tickets with a manager reply that have not been analysed. New tickets are also picked up automatically.'
      return 'Only the first reply written by a manager is rated; automatic messages are never scored.'
    },
    emptyTitle() {
      if (this.hasFilters) return 'No tickets match these filters'
      return this.view === 'pending' ? 'No tickets are waiting for analysis' : 'No tickets have been analysed yet'
    },
    emptyText() {
      if (this.hasFilters) return 'Try another search or clear the filters.'
      if (this.view === 'pending') return 'Tickets appear here when a manager has replied to a customer.'
      return 'Run an analysis from the Pending set to see scores here.'
    }
  },
  watch: {
    listKey: { immediate: true, handler() { this.selected = []; this.load() } },
    countsKey: { immediate: true, handler() { this.loadCounts() } },
    'query.search'(value) { clearTimeout(this.searchTimer); this.search = value || '' }
  },
  created() { this.search = this.query.search || ''; this.loadManagers() },
  mounted() {
    this.pollTimer = setInterval(this.poll, 5000)
  },
  beforeDestroy() {
    clearInterval(this.pollTimer)
    clearTimeout(this.searchTimer)
    this.generation++
    this.countsGeneration++
  },
  methods: {
    dateLabel,
    isActive,
    scoreTone,
    statusOf,
    async loadManagers() {
      try {
        const response = await getTrengoFirstReplyManagers()
        this.managerOptions = response.data
      } catch (error) {
        this.managerOptions = []
      }
    },
    paramsFor(view, forCount = false) {
      const params = { search: this.query.search, manager: this.query.manager }
      if (view === 'pending') params.historical = this.historical ? 'true' : 'false'
      else Object.assign(params, { status: this.query.status, score_min: this.query.score_min, score_max: this.query.score_max })
      if (forCount) return cleanQuery({ ...params, page: 1, page_size: 1 })
      return cleanQuery({ ...params, page: this.page, page_size: PAGE_SIZE })
    },
    fetcher(view) { return view === 'pending' ? getTrengoFirstReplyCandidates : getTrengoFirstReplyRatings },
    async load(silent = false) {
      const generation = ++this.generation
      if (!silent) { this.loading = true; this.error = '' }
      try {
        const response = await this.fetcher(this.view)(this.params)
        if (generation !== this.generation) return
        this.rows = response.data.results
        this.count = response.data.count
        this.pages = response.data.pages || 1
        this.error = ''
      } catch (error) {
        if (generation !== this.generation) return
        if (error && error.response && error.response.status === 404 && this.page > 1) {
          this.navigate({ ...this.query, page: undefined }, true)
          return
        }
        this.error = errorMessage(error, 'Tickets')
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },
    async loadCounts() {
      const generation = ++this.countsGeneration
      try {
        const [analysed, pending] = await Promise.all([
          getTrengoFirstReplyRatings(this.paramsFor('analysed', true)),
          getTrengoFirstReplyCandidates(this.paramsFor('pending', true))
        ])
        if (generation !== this.countsGeneration) return
        this.counts = { analysed: analysed.data.count, pending: pending.data.count }
      } catch (error) {
        if (generation === this.countsGeneration) this.counts = { analysed: '—', pending: '—' }
      }
    },
    refresh() { this.load(true); this.loadCounts() },
    poll() {
      if (document.hidden || this.loading || this.error) return
      if (this.view === 'analysed' && this.rows.some(isActive)) this.refresh()
    },
    navigate(query, replace = false) {
      return this.$router[replace ? 'replace' : 'push']({ path: this.$route.path, query: cleanQuery(query) }).catch(error => {
        if (error.name !== 'NavigationDuplicated') this.error = 'Unable to update this view. Try again.'
      })
    },
    queueSearch(value) {
      this.search = value || ''
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.submitSearch, 300)
    },
    submitSearch() {
      clearTimeout(this.searchTimer)
      this.changeFilters({ search: this.search.trim() })
    },
    changeFilters(patch) {
      this.navigate({ ...this.query, ...patch, page: undefined })
    },
    changeView(view) { this.navigate({ view: view === 'pending' ? 'pending' : undefined }) },
    changeScope(scope) { this.changeFilters({ historical: scope === 'historical' ? 'true' : undefined }) },
    changePage(page) { this.navigate({ ...this.query, page: String(page) }) },
    clearFilters() {
      clearTimeout(this.searchTimer)
      this.search = ''
      this.navigate({ view: this.query.view })
    },
    toggleAll(checked) { this.selected = checked ? this.rows.map(row => row.id) : [] },
    toggleOne(id, checked) {
      this.selected = checked ? [...this.selected.filter(item => item !== id), id] : this.selected.filter(item => item !== id)
    },
    async run(ids) {
      if (!this.canEdit || this.running || !ids.length) return
      this.running = true
      this.busy = [...ids]
      const button = this.$refs.runSelected
      if (button && ids === this.selected) button.preload = true
      try {
        await postTrengoFirstReplyRun(ids)
        this.$notify({ type: 'success', title: ids.length === 1 ? 'Analysis queued' : `${ids.length} analyses queued` })
        this.selected = []
        this.refresh()
      } catch (error) {
        this.$notify({ type: 'error', title: errorMessage(error, 'Tickets') })
      } finally {
        this.running = false
        this.busy = []
        if (this.$refs.runSelected) this.$refs.runSelected.preload = false
      }
    },
    async rerun(row) {
      if (!this.canEdit || this.busy.includes(row.id)) return
      this.busy = [...this.busy, row.id]
      try {
        await postTrengoFirstReplyRerun(row.id)
        this.$notify({ type: 'success', title: 'Analysis queued' })
        this.refresh()
      } catch (error) {
        this.$notify({ type: 'error', title: errorMessage(error, 'Rating') })
      } finally {
        this.busy = this.busy.filter(id => id !== row.id)
      }
    },
    openRow({ row }) {
      if (this.view === 'analysed') this.navigate({ ...this.query, rating: row.id })
    },
    closeRating() {
      this.navigate({ ...this.query, rating: undefined }, true)
      this.$nextTick(() => this.$refs.page && this.$refs.page.focus({ preventScroll: true }))
    }
  }
}
</script>

<style lang="scss" scoped>
.first-reply { outline: none; }
.first-reply__field { flex: 0 0 $ui-filter-control-width; width: $ui-filter-control-width; min-width: 0; max-width: 100%; }
.first-reply__check :deep(.checkbox__text) { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
</style>
